import { useState } from "react";
import { Logo } from "../atoms/Logo.jsx";
import { supabase } from "../lib/supabase.js";
import { createApplication } from "../services/persona-owner.js";

/**
 * Persona Owner Signup — email/password registration for elected officials.
 * After signup, the user must verify their email before proceeding.
 * Role assignment happens during admin approval (ONB-P4, Phase 3).
 */
export function PersonaOwnerSignup({ onSuccess, onLogin, onCancel }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // emailRedirectTo sends the user to /manage after confirming email
      // so they land on the persona owner login (not the citizen onboarding)
      const origin = window.location.origin;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${origin}/manage?from=signup`,
          data: {
            role: "persona_owner",
            full_name: fullName,
          },
        },
      });

      if (error) {
        setErr(error.message || "Signup failed");
        setLoading(false);
        return;
      }

      // If session is immediately available (auto-confirm enabled),
      // create application and go straight to claim form
      if (data?.session) {
        try {
          const { application } = await createApplication();
          onSuccess?.(application);
          return;
        } catch (appErr) {
          // Application creation failed — still redirect, they can retry
          onSuccess?.(null);
          return;
        }
      }

      // Otherwise email verification is required
      setSuccess(true);
    } catch {
      setErr("Signup failed \u2014 please try again");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: "100vh", background: "#090e1a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px" }}>
        <div style={{ width: "100%", maxWidth: 380, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
          <h2 style={{ color: "#f1f5f9", fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Check Your Email</h2>
          <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            We sent a verification link to <strong style={{ color: "#e2e8f0" }}>{email}</strong>.
            Click the link to verify your account, then log in.
          </p>
          <button onClick={onLogin} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none", borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#090e1a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px" }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ margin: "0 auto 14px", display: "flex", justifyContent: "center" }}><Logo size={32} /></div>
        </div>

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", color: "#10b981", textTransform: "uppercase", marginBottom: 6 }}>Persona Owner</div>
          <h2 style={{ color: "#f1f5f9", fontSize: 22, fontWeight: 900, fontFamily: "Georgia,serif", margin: 0 }}>Claim Your AI Clone</h2>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 8 }}>Create an account to manage your digital persona on QuestionMe.</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>Full Name</div>
            <input
              autoFocus
              value={fullName}
              onChange={(e) => { setFullName(e.target.value); setErr(""); }}
              placeholder="Senator Jane Smith"
              style={{ width: "100%", padding: "13px 16px", background: "#0d1724", border: `1px solid ${err ? "#ef4444" : "#1e2d45"}`, borderRadius: 11, color: "#e2e8f0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>Email</div>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(""); }}
              placeholder="you@gov.ng"
              style={{ width: "100%", padding: "13px 16px", background: "#0d1724", border: `1px solid ${err ? "#ef4444" : "#1e2d45"}`, borderRadius: 11, color: "#e2e8f0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>Password</div>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErr(""); }}
              placeholder="Min 8 characters"
              style={{ width: "100%", padding: "13px 16px", background: "#0d1724", border: `1px solid ${err ? "#ef4444" : "#1e2d45"}`, borderRadius: 11, color: "#e2e8f0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>Confirm Password</div>
            <input
              type="password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setErr(""); }}
              placeholder="Confirm password"
              style={{ width: "100%", padding: "13px 16px", background: "#0d1724", border: `1px solid ${err ? "#ef4444" : "#1e2d45"}`, borderRadius: 11, color: "#e2e8f0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
          </div>

          {err && <div style={{ color: "#ef4444", fontSize: 13 }}>{err}</div>}

          <button type="submit" disabled={loading || !email || !password || !fullName} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none", borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginTop: 4, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Creating account\u2026" : "Create Account"}
          </button>

          <div style={{ textAlign: "center", fontSize: 13, marginTop: 4 }}>
            <span style={{ color: "#64748b" }}>Already have an account? </span>
            <button type="button" onClick={onLogin} style={{ background: "none", border: "none", color: "#10b981", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
              Log in
            </button>
          </div>

          <button type="button" onClick={onCancel} style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid #1e2d45", color: "#64748b", borderRadius: 11, fontSize: 14, cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>
            Back to citizen view
          </button>
        </form>
      </div>
    </div>
  );
}

export default PersonaOwnerSignup;
